import React from "react";

export default function PaymentManagementSection() {
  const features = [
    "Quotation Value",
    "Work Order Value",
    "Advance Payment",
    "Milestone Payments",
    "Balance Payment",
    "Invoice Management",
    "Payment Status",
    "Transaction History",
    "Download Invoices",
    "Payment Reports",
  ];

  const tags = [
    "Payment Pending",
    "Advance Received",
    "Partially Paid",
    "Processing",
    "Fully Paid",
  ];

  const timeline = [
    { number: 1, label: "Quotation" },
    { number: 2, label: "Work Order" },
    { number: 3, label: "Advance" },
    { number: 4, label: "Milestone" },
    { number: 5, label: "Final Invoice" },
    { number: 6, label: "Final Payment" },
  ];

  const icon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f7941d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="13" rx="2" />
      <path d="M2 10h20" />
      <circle cx="16" cy="14.5" r="1.6" />
    </svg>
  );

  return (
    <>
      <ResponsiveStyles />
      <section className="pm-section" style={styles.section}>
        <div className="" style={styles.wrap}>
          <div className="pm-header" style={styles.header}>
            <span className="pm-eyebrow" style={styles.eyebrow}>
              <span style={styles.eyebrowDot} />
              PAYMENTS &amp; FINANCIAL MANAGEMENT
            </span>

            <h2 className="pm-heading" style={styles.heading}>
              Transparent project &amp;{" "}
              <span style={styles.accent}>payment management.</span>
            </h2>

            <p className="pm-sub" style={styles.subheading}>
              Track the complete financial lifecycle of every project — from
              quotation to final payment.
            </p>
          </div>

          <div className="pm-panels" style={styles.panels}>
            {/* LEFT: white feature card */}
            <div className="pm-white-card" style={styles.whiteCard}>
              <div className="pm-feature-grid" style={styles.featureGrid}>
                {features.map((f, i) => (
                  <div className="pm-feature-item" style={styles.featureItem} key={i}>
                    <span className="pm-feature-icon" style={styles.featureIcon}>
                      {icon}
                    </span>
                    <span className="pm-feature-text" style={styles.featureText}>
                      {f}
                    </span>
                  </div>
                ))}
              </div>

              <div className="pm-tags-row" style={styles.tagsRow}>
                {tags.map((t, i) => (
                  <span className="pm-tag" style={styles.tag} key={i}>
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT: dark timeline card */}
            <div className="pm-dark-card" style={styles.darkCard}>
              <span className="pm-timeline-label" style={styles.timelineLabel}>
                PROJECT FINANCIAL TIMELINE
              </span>

              <div className="pm-timeline-list" style={styles.timelineList}>
                {timeline.map((step, i) => (
                  <div className="pm-timeline-item" style={styles.timelineItem} key={i}>
                    <span className="pm-timeline-number" style={styles.timelineNumber}>
                      {step.number}
                    </span>
                    <span className="pm-timeline-text" style={styles.timelineText}>
                      {step.label}
                    </span>
                    <svg
                      className="pm-timeline-arrow"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="rgba(255,255,255,0.4)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </div>
                ))}
              </div>

              <p className="pm-timeline-note" style={styles.timelineNote}>
                Payment terms are configurable by APIC Admin and agreed
                between the parties.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Responsive rules. Below 1024px the two panels stack (flex column) and
 * the feature grid drops from 2 columns to 1, since a fixed 2-col grid
 * gets cramped once the panel itself is full width on a phone.
 */
function ResponsiveStyles() {
  return (
    <style>{`
      @media (max-width: 1024px) {
        .pm-panels {
          display: flex !important;
          flex-direction: column !important;
        }
        .pm-white-card, .pm-dark-card {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
      }

      @media (max-width: 640px) {
        .pm-section {
          padding: 48px 0 !important;
          min-height: auto !important;
        }
        .pm-wrap {
          padding: 0 18px !important;
        }
        .pm-eyebrow {
          font-size: 10px !important;
          padding: 7px 12px !important;
        }
        .pm-heading {
          font-size: clamp(1.6rem, 7vw, 2.2rem) !important;
          line-height: 1.25 !important;
        }
        .pm-sub {
          font-size: 0.95rem !important;
          padding: 0 6px !important;
        }
        .pm-panels {
          margin-top: 32px !important;
          gap: 20px !important;
        }
        .pm-white-card {
          padding: 20px !important;
          border-radius: 18px !important;
        }
        .pm-feature-grid {
          display: flex !important;
          flex-direction: column !important;
          grid-template-columns: none !important;
          gap: 10px !important;
        }
        .pm-feature-item {
          padding: 12px 14px !important;
        }
        .pm-feature-text {
          font-size: 0.92rem !important;
        }
        .pm-tags-row {
          margin-top: 16px !important;
          gap: 8px !important;
        }
        .pm-tag {
          font-size: 0.78rem !important;
          padding: 6px 12px !important;
        }
        .pm-dark-card {
          padding: 22px !important;
          border-radius: 18px !important;
        }
        .pm-timeline-item {
          padding: 10px 0 !important;
        }
        .pm-timeline-number {
          width: 30px !important;
          height: 30px !important;
          font-size: 0.9rem !important;
        }
        .pm-timeline-text {
          font-size: 0.95rem !important;
        }
        .pm-timeline-note {
          font-size: 0.82rem !important;
        }
      }

      @media (max-width: 380px) {
        .pm-heading {
          font-size: 1.5rem !important;
        }
      }
    `}</style>
  );
}

const styles = {
  section: {
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    background: "linear-gradient(180deg, #f4f7fc 0%, #eef1f8 100%)",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    boxSizing: "border-box",
    padding: "72px 0",
  },
  wrap: {
    width: "100%",
    maxWidth: 1280,
    margin: "0 auto",
    padding: "0 32px",
    boxSizing: "border-box",
  },
  header: {
    textAlign: "center",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "9px 18px",
    borderRadius: 999,
    background: "#fdecd3",
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: "#0b1330",
  },
  eyebrowDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#f7941d",
    flexShrink: 0,
  },
  heading: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 800,
    fontSize: "clamp(2rem, 3.8vw, 3rem)",
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    color: "#0b1330",
    margin: "22px 0 0",
  },
  accent: {
    color: "#f7941d",
  },
  subheading: {
    marginTop: 18,
    fontSize: "1.08rem",
    lineHeight: 1.7,
    color: "#5a6280",
    maxWidth: 720,
    marginLeft: "auto",
    marginRight: "auto",
  },
  panels: {
    marginTop: 48,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 28,
    alignItems: "stretch",
  },
  whiteCard: {
    background: "#ffffff",
    border: "1px solid #e7eaf3",
    borderRadius: 22,
    padding: "28px",
    boxShadow: "0 24px 48px -32px rgba(11,19,48,0.18)",
    boxSizing: "border-box",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 14,
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#f6f8fc",
    border: "1px solid #e7eaf3",
    borderRadius: 12,
    padding: "14px 16px",
    boxSizing: "border-box",
  },
  featureIcon: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  featureText: {
    fontSize: "0.98rem",
    fontWeight: 500,
    color: "#0b1330",
  },
  tagsRow: {
    marginTop: 20,
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  tag: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 700,
    fontSize: "0.85rem",
    color: "#0b1330",
    border: "1px solid #e2c896",
    borderRadius: 999,
    padding: "8px 16px",
    background: "#fffdf8",
  },
  darkCard: {
    background: "linear-gradient(160deg, #0b1330 0%, #0e1a42 100%)",
    borderRadius: 22,
    padding: "30px",
    boxShadow: "0 30px 60px -30px rgba(0,0,0,0.4)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  timelineLabel: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 700,
    fontSize: "0.75rem",
    letterSpacing: "0.1em",
    color: "#9aa2c4",
  },
  timelineList: {
    marginTop: 20,
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  timelineItem: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "13px 0",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  timelineNumber: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f7941d, #ef7f0e)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 800,
    fontSize: "1rem",
    color: "#1a1204",
    flexShrink: 0,
  },
  timelineText: {
    flex: 1,
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    fontWeight: 600,
    fontSize: "1.02rem",
    color: "#ffffff",
  },
  timelineNote: {
    marginTop: 22,
    fontSize: "0.88rem",
    lineHeight: 1.6,
    color: "#8a93b8",
  },
};